import { coversTime, normalizeWeekHours, tenantClock } from "@/lib/scheduling/normalize-hours"
import { DayOfWeek, ServiceHours } from "@/types"

/** Maps `Date.prototype.getDay()` indexes (0 = Sunday) to API day names. */
export const DAY_MAP: DayOfWeek[] = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
]

/**
 * Local wall-clock time as "HH:MM", the granularity every schedule comparison
 * in this app runs at.
 *
 * Seconds are dropped on purpose. Schedules are authored in HH:MM — the
 * merchant portal has no seconds field — so a window that ends at 23:00 means
 * "through 23:00"; closing at 23:00:01 compares at a precision the merchant
 * never expressed. Mirrors the backend (`Service.minutesOfDay` and
 * `TimeOfDay.TruncateToMinute` in merchant-service-core), which must agree with
 * this file or the storefront and the store status contradict each other.
 *
 * Read from the Date components rather than a locale formatter: the local hour
 * is exactly what is wanted (a Brazilian customer's clock is Brazil time), and
 * `toLocaleTimeString("en-US", { hour12: false })` is the h24 variant, which
 * renders midnight as "24:00" and would break every comparison after 00:00.
 */
export function localTimeHHMM(now: Date = new Date()): string {
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`
}

/**
 * Normalizes a window bound ("HH:MM:SS" or "HH:MM") to "HH:MM".
 *
 * Bounds carry seconds because the wire format renders `time` columns as
 * HH:MM:SS, not because anyone chose those seconds — so both sides of a
 * comparison are truncated, never just the clock.
 */
export function timeBoundHHMM(bound: string): string {
  return bound.slice(0, 5)
}

/**
 * Returns whether the given instant falls inside any of the week-hours
 * windows, on the TENANT's clock. Empty or missing schedules count as closed.
 *
 * Both sides go through `normalizeWeekHours`, so a UTC time-of-day payload and
 * a legacy wall-clock one answer the same question (§9.1). Boundaries are
 * inclusive on both ends, at minute granularity.
 */
export function isNowInSchedule(
  weekHours: ServiceHours["weekHours"],
  now: Date = new Date(),
  timeZone?: string | null,
): boolean {
  if (!weekHours?.length) return false
  const clock = tenantClock(now, timeZone)

  return normalizeWeekHours(weekHours, timeZone).some(
    (window) => window.day === clock.day && coversTime(window, clock.time),
  )
}

type WeekHours = ServiceHours["weekHours"] | null | undefined

/**
 * Input of both closing lookups. Pass `weekHours` for one channel, or
 * `schedules` to pick the latest end among several (delivery + takeout).
 */
export interface CurrentClosing {
  now?: Date
  weekHours?: WeekHours
  schedules?: WeekHours[]
  /** Tenant zone the windows are read on; the registered one when omitted. */
  timeZone?: string | null
}

/**
 * End time of the current open window, as "HH:mm". When several windows cover
 * now, the LATEST end wins — the open-until label for the customer still being
 * served.
 */
export function getCurrentClosingHHMM({
  weekHours,
  now = new Date(),
  timeZone,
}: CurrentClosing): string | null {
  if (!weekHours?.length) return null
  const clock = tenantClock(now, timeZone)

  let latest: string | null = null
  for (const window of normalizeWeekHours(weekHours, timeZone)) {
    if (window.day !== clock.day) continue
    if (!coversTime(window, clock.time)) continue
    const end = timeBoundHHMM(window.endTime)
    if (!latest || end > latest) latest = end
  }
  return latest
}

/** Latest closing among several schedules that are open right now. */
export function getLatestCurrentClosing({
  schedules = [],
  now = new Date(),
  timeZone,
}: CurrentClosing): string | null {
  let latest: string | null = null
  for (const weekHours of schedules) {
    const end = getCurrentClosingHHMM({ weekHours, now, timeZone })
    if (end && (!latest || end > latest)) latest = end
  }
  return latest
}
